import React, { useMemo, useState } from 'react';
import { Drawer, useToasts } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import {
  AccountInfoType,
  RangeLabelType,
  SurveyBlockCategoryType,
  SurveyBlockWithQuestionType,
  SurveyQuestionType,
  SurveyResponseType,
  SurveyType,
  UserType,
} from 'shared/types';
import { useGetContact, useUpdateResponseMutation } from '../../../hooks';
import ResponseNotes from './ResponseNotes';
import AddResponseNote from './ResponseNotes/add-response-note';
import ResponseActions from './response-actions';
import ResponseCard from './response-card';
import ResponseCardLoader from './response-card-loader';
import EditResponseDrawer from './response-edit';
import TransactionProperties from './transaction-properties';

type ResponseDetailProps = {
  responseData?: SurveyResponseType;
  data?: any;
  responseId: string;
  accountInfo?: AccountInfoType;
  surveyInfo?: SurveyType;
  showContactSection: boolean;
  placeholders: any;
  users: any;
  isResponseLoaded: boolean;
  userInfo: UserType;
};

const ResponseDetail = (props: ResponseDetailProps) => {
  const { t } = useTranslation();
  const {
    responseData,
    responseId,
    surveyInfo,
    placeholders,
    users,
    accountInfo,
    isResponseLoaded,
    userInfo,
  } = props;
  const [, setToast] = useToasts();
  const { mutate: updateResponse } = useUpdateResponseMutation(setToast);
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [editableResponseFields, setEditableResponseFields] =
    useState<any>(null);
  const [isEditNoteEnabled, setIsEditNoteEnabled] = useState(false);
  const [contactName, setContactName] = useState('');
  const {
    data: contactData = {},
    isLoading: isLoadingContact,
    isError: isContactError,
    isFetching: isContactFetching,
  } = useGetContact(responseData?.ucr_contact_id || '', {
    refetchOnWindowFocus: true,
  });

  const allPlaceholders = useMemo<Record<string, string>>(
    () => ({
      '{{nps.rating}}': responseData?.nps_rating,
      '{{csat.rating}}': responseData?.csat_rating,
      '{{ces.rating}}': responseData?.ces_rating,
      '{{contact.first_name}}': contactData?.first_name,
      '{{contact.last_name}}': contactData?.last_name,
      '{{contact.work_email}}': contactData?.work_email,
      ...(placeholders.placeholders || {}),
    }),
    [placeholders.placeholders, contactData, responseData],
  );
  const { surveyBlocks, isEditEnabled } = useMemo<{
    surveyBlocks: SurveyBlockWithQuestionType[];
    isEditEnabled: boolean;
  }>(() => {
    const { question_details, blocks } = surveyInfo || {};
    const surveyQuestions = question_details?.questions || [];
    const newBlocks: SurveyBlockWithQuestionType[] = (blocks || [])?.map(
      (block) => {
        if (block.question_name) {
          const foundQuestion = surveyQuestions.find(
            (q) => q.name === block.question_name,
          );
          return {
            ...block,
            question: foundQuestion,
          } as SurveyBlockWithQuestionType;
        } else {
          const foundQuestion = surveyQuestions.find(
            (q) => q.name === block?.category?.[0].question_name,
          );
          return {
            ...block,
            question: foundQuestion,
          };
        }
      },
    );
    return {
      surveyBlocks:
        (newBlocks || [])?.filter((b: any) => b.name !== 'TY') || [],
      isEditEnabled: !!(newBlocks || [])?.find(
        (b: SurveyBlockWithQuestionType) =>
          b.question?.type_info?.question_type === 'PARAGRAPH' ||
          b.question?.type_info?.question_type === 'TEXT',
      ),
    };
  }, [surveyInfo, isResponseLoaded]);

  const rating = responseData?.[`${surveyInfo?.type?.toLowerCase()}_rating`];

  const { ratingType, ratingTypeId } = React.useMemo<{
    ratingType?: RangeLabelType | 'loading';
    ratingTypeId?: string;
  }>(() => {
    const themeQuestion = surveyInfo?.question_details?.questions?.find(
      (que: SurveyQuestionType) => !!que.type_info?.pivot_question,
    );
    if (themeQuestion && responseData && surveyInfo) {
      const type = themeQuestion?.type_info?.scale_properties?.range?.find(
        (branch) => {
          return rating >= Number(branch.min) && rating <= Number(branch.max);
        },
      );
      const ratingGroupName = `${surveyInfo.type?.toLowerCase()}_rating_group`;
      const ratingType: RangeLabelType =
        responseData[ratingGroupName]?.toLowerCase() || type?.label;

      return {
        ratingType,
        ratingTypeId: type?.id || undefined,
      };
    }
    return { ratingType: 'loading', ratingTypeId: undefined };
  }, [responseData, surveyInfo]);

  const handleUpdate = (editedResponse: any) => {
    const updatedEntries: any = {};
    Object.keys(editedResponse).forEach((key) => {
      updatedEntries[key] = editedResponse[key].value;
    });
    const updatedData = {
      ...responseData,
      question_answers: {
        ...responseData?.question_answers,
        ...updatedEntries,
      },
    };

    updateResponse({
      surveyId: surveyInfo?.id || '',
      responseId,
      updatedData,
      edited: updatedEntries,
      commentQuestion:
        Object.keys(editedResponse).find(
          (item: string) => editedResponse[item].comment,
        ) || null,
    });
    setEditOpen(false);
  };

  React.useEffect(() => {
    if (responseData && isEditEnabled) {
      const temp: any = {};
      surveyBlocks.forEach((block: any) => {
        const { question_type, category } = block;
        if (question_type === 'TEXT' || question_type === 'PARAGRAPH') {
          const question = block?.category
            ? category.find(
                (item: SurveyBlockCategoryType) => item.id === ratingTypeId,
              )?.question_name
            : block.question_name;

          temp[question] = {
            required: block?.required,
            value: responseData?.question_answers[question] || '',
            question_type,
            comment: block?.comment,
            is_deleted: block?.is_deleted,
          };
        }
      });
      setEditableResponseFields(temp);
    }
  }, [responseData, isEditEnabled, surveyBlocks, ratingTypeId]);

  React.useEffect(() => {
    if (contactData && !isLoadingContact && !isContactError) {
      const name =
        contactData?.first_name || contactData?.last_name
          ? `${contactData?.first_name || ''} ${contactData?.last_name || ''}`
          : contactData?.work_email
          ? contactData?.work_email
          : responseData?.contact_id
          ? `${t('deleted_contact')}`
          : `${t('anonymous')}`;
      setContactName(name);
    } else if (isContactError) {
      setContactName('Deleted contact');
    }
  }, [contactData, isLoadingContact, isContactError, isContactFetching]);

  return (
    <div className="response-view response-detail">
      <div className="response-view__body">
        <ResponseActions
          ratingType={ratingType}
          data={responseData}
          isResponseLoaded={isResponseLoaded}
          surveyInfo={surveyInfo}
        />

        <section className="container">
          {isResponseLoaded && responseData?.id && surveyInfo?.id ? (
            <>
              <ResponseCard
                rating={rating}
                ratingType={ratingType || 'loading'}
                ratingTypeId={ratingTypeId || ''}
                surveyBlocks={surveyBlocks}
                surveyQuestions={surveyInfo?.question_details?.questions || []}
                contactName={contactName}
                isLoadingContact={isLoadingContact || isContactFetching}
                responseData={responseData}
                surveyInfo={surveyInfo}
                accountInfo={accountInfo}
                placeholders={allPlaceholders}
              />

              {surveyInfo?.id ? (
                <>
                  {responseData?.transaction_id ? (
                    <TransactionProperties
                      surveyId={surveyInfo?.id}
                      transactionId={responseData?.transaction_id}
                    />
                  ) : null}
                  <ResponseNotes
                    surveyId={surveyInfo?.id}
                    responseId={responseData?.id}
                    accountInfo={accountInfo}
                    users={users}
                    setIsEditNoteEnabled={setIsEditNoteEnabled}
                    userInfo={userInfo}
                  />
                </>
              ) : null}
            </>
          ) : (
            <ResponseCardLoader />
          )}
        </section>
      </div>

      {isResponseLoaded && surveyInfo?.id && responseData ? (
        <AddResponseNote
          surveyId={surveyInfo?.id}
          useNoteToggleButton
          responseId={responseData?.id}
          showContactSection={props.showContactSection}
          inlineMode={false}
          disabled={isEditNoteEnabled}
        />
      ) : null}

      <Drawer
        isOpen={editOpen}
        onDismiss={() => setEditOpen(false)}
        isFullHeight
        size="medium"
        placement="right"
      >
        <EditResponseDrawer
          editableResponseQuestions={editableResponseFields}
          surveyQuestions={surveyInfo?.question_details?.questions || []}
          setEditOpen={setEditOpen}
          handleUpdate={handleUpdate}
          responseData={responseData}
          placeholders={allPlaceholders}
        />
      </Drawer>
    </div>
  );
};

export default ResponseDetail;
